// What a part set into a wall cuts there (`inWall`: a control unit's display —
// its flush box in a wall drawn with the Wall tool, its hole in a sauna's wood;
// products.md, *Mounted by where it is dropped*). Derived from the parts every
// read, never stored: a part moved, its cut moves with it; a part gone, its cut
// is too. This module finds where each part meets its wall; wallFeatures.ts
// (`wallPockets`) and polyRoom.ts (`roomWallCuts`) turn that into each wall's
// own terms, which every view reads. Pure and DOM-free (test/wallCuts.test.mjs).

import type {
  ProductInstance,
  Vec2,
  WallCut,
} from '../shared/types';
import { partHeight } from './heights';
import { isTilted } from './tilt';

/** How near a wall's face a part's own face must lie, and how square to it, to
 *  be set into it (mm; the dot of its into-the-wall direction with the face's). */
export const CUT_FACE_TOL = 1;
export const CUT_SQUARE = 0.999;

/** A cut's bottom and top above the ground for a part: from its `base` over the
 *  part's underside, else centred on its middle. */
export function cutBand(part: Pick<InWallPart, 'mid' | 'base'>, cut: Pick<WallCut, 'height' | 'base'>): [number, number] {
  const z0 = cut.base != null ? part.base + cut.base : part.mid - cut.height / 2;
  return [z0, z0 + cut.height];
}

/** A part set into a wall, where it meets the wall: the world point of its
 *  middle on the face it stands out of, the unit direction into the wall, its
 *  middle's height above the ground, and what it cuts in either kind of wall. */
export interface InWallPart {
  src: string;
  at: Vec2;
  into: Vec2;
  mid: number;
  /** Its underside's height above the ground. */
  base: number;
  wallCut?: WallCut;
  roomWallCut?: WallCut;
}

/** Where a part set into a wall (`inWall`) meets it: the world point of its
 *  middle on the face it stands out of and the unit direction into the wall.
 *  Null for any other part, and for a tilted copy (it stands out of no wall). */
export function inWallFace(p: ProductInstance): { at: Vec2; into: Vec2 } | null {
  if (p.inWall == null || p.autoBenches || isTilted(p)) return null;
  const r = (p.rotation * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
  const face = p.depth / 2 - p.inWall;
  return { at: { x: p.x - s * face, y: p.y + c * face }, into: { x: s, y: -c } };
}

/** The parts set into walls: those with an `inWall` band and a cut, upright (a
 *  tilted copy stands out of no wall). */
export function inWallParts(instances: readonly ProductInstance[]): InWallPart[] {
  const out: InWallPart[] = [];
  for (const p of instances) {
    const f = !(p.wallCut || p.roomWallCut) ? null : inWallFace(p);
    if (!f) continue;
    out.push({
      src: p.id,
      at: f.at,
      into: f.into,
      mid: (p.heightFromGround ?? 0) + partHeight(p) / 2,
      base: p.heightFromGround ?? 0,
      ...(p.wallCut ? { wallCut: p.wallCut } : {}),
      ...(p.roomWallCut ? { roomWallCut: p.roomWallCut } : {}),
    });
  }
  return out;
}
